// Shared vocabulary for authored transition sequences.
//
// The Welcome → Menu tape transport was the first of these and, for a while,
// the only one — so its score, its interpolation helpers and its orchestration
// all lived together in one file. Adding the FIGHT intro made the split
// obvious: everything in THIS file is true of any authored sequence, and
// everything in `tapeTimeline.ts` / `spliceTimeline.ts` is true of one.
//
// A sequence is a pure function of elapsed time. Nothing here touches the DOM,
// React, or an animation library, which is what makes the timing testable and
// the whole performance reproducible.

import type { MotionTier } from './motion';

export interface Beat {
  /** Seconds from sequence start. */
  at: number;
  /** Seconds. A zero-duration beat is skipped entirely by the orchestrator. */
  dur: number;
}

/**
 * The contract every score must satisfy, because the orchestrator depends on
 * exactly these fields: it needs to know when to swap the route, when the cover
 * is safe to swap under, and when the whole thing is over.
 *
 * Individual scores add their own beats on top (the transport has spinUp /
 * scrub / thread; the splice has stop / slam).
 */
export interface SequenceScore {
  tier: MotionTier;
  /** The cover closing over the screen. */
  cover: Beat;
  /**
   * Seconds from start at which the route commit fires. MUST fall inside
   * `coverOpaqueWindow` — otherwise the player watches the destination pop in,
   * which is the one failure mode that makes a sequence look broken rather
   * than merely mistimed.
   */
  commitAt: number;
  /** The cover retracting onto the destination. */
  settle: Beat;
  /** Seconds. End of the sequence. */
  total: number;
  /**
   * False on the reduced tier: no translation, rotation or parallax. The
   * sequence still plays — same beats, same graphic payload — it just
   * dissolves rather than travels.
   */
  travels: boolean;
}

export function beatEnd(b: Beat): number {
  return b.at + b.dur;
}

/**
 * The interval during which the cover is fully opaque, i.e. when it is safe to
 * swap the route underneath. Asserted against `commitAt` in every score's tests.
 */
export function coverOpaqueWindow(s: SequenceScore): [number, number] {
  return [beatEnd(s.cover), s.settle.at];
}

// ---------------------------------------------------------------------------
// Track sampling
// ---------------------------------------------------------------------------

export interface Stop {
  /** Seconds from sequence start. */
  at: number;
  /** Value at this stop. */
  v: number;
  /** Easing INTO this stop from the previous one. Defaults to linear. */
  ease?: (x: number) => number;
}

/**
 * Piecewise interpolation of a stop list at time `t` (seconds).
 *
 * Clamps outside the defined range rather than extrapolating: a track
 * describes a finite performance, and a value invented past the end of it
 * would be a bug every time.
 */
export function sampleTrack(stops: Stop[], t: number): number {
  if (stops.length === 0) return 0;
  if (t <= stops[0].at) return stops[0].v;
  const last = stops[stops.length - 1];
  if (t >= last.at) return last.v;

  for (let i = 1; i < stops.length; i++) {
    const b = stops[i];
    if (t > b.at) continue;
    const a = stops[i - 1];
    const span = b.at - a.at;
    // Zero-length segment: jump straight to the later value rather than
    // dividing by zero.
    if (span <= 0) return b.v;
    const x = (t - a.at) / span;
    return a.v + (b.v - a.v) * (b.ease ? b.ease(x) : x);
  }
  return last.v;
}

// ---------------------------------------------------------------------------
// Scaling
// ---------------------------------------------------------------------------

function isBeat(v: unknown): v is Beat {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Beat).at === 'number' &&
    typeof (v as Beat).dur === 'number'
  );
}

/**
 * Stretch every time in a score by `factor`, leaving its shape and decorative
 * budget untouched.
 *
 * Beats are discovered structurally rather than listed, so this works for any
 * score without needing to know its extra beats — a score that adds a new beat
 * gets it scaled for free, and cannot be silently forgotten here.
 */
export function scaleSequence<T extends SequenceScore>(score: T, factor: number): T {
  if (factor === 1) return score;
  const out = { ...score } as Record<string, unknown>;
  for (const [key, value] of Object.entries(out)) {
    if (isBeat(value)) {
      out[key] = { at: value.at * factor, dur: value.dur * factor };
    }
  }
  out.commitAt = score.commitAt * factor;
  out.total = score.total * factor;
  return out as T;
}

/**
 * Dev-only slow-motion, shared by every sequence. In a browser console:
 *
 *     window.__SEQ_SPEED__ = 6   // run sequences at 1/6 speed
 *     window.__SEQ_SPEED__ = 1   // back to normal
 *
 * Exists because a sub-second sequence cannot be judged from screenshots at
 * full speed, and because tuning beats should not require a rebuild. Ignored
 * outside the browser and a no-op unless explicitly set. `__TAPE_SPEED__` is
 * still honoured as an alias — it was the original name and is referenced in
 * docs/motion-audit-first-screens.md.
 */
export function devSpeedFactor(): number {
  if (typeof window === 'undefined') return 1;
  const w = window as unknown as { __SEQ_SPEED__?: unknown; __TAPE_SPEED__?: unknown };
  const raw = w.__SEQ_SPEED__ ?? w.__TAPE_SPEED__;
  return typeof raw === 'number' && raw > 0 && raw <= 20 ? raw : 1;
}

export const ALL_TIERS: MotionTier[] = ['full', 'lowPower', 'reduced'];
